import { creditNoteService, talentService, emailService } from '../../../api/apiService';
import { buildCreditNoteEmailHtml, buildCreditNoteCancellationEmailHtml } from './creditNoteEmailTemplate';

/**
 * The two documents a talent can receive. Both take the identical route — fetch the credit
 * note, resolve the talent's address, attach the rendered PDF — and differ only in wording and
 * attachment name, so they share one sender below.
 */
const DOCUMENT_KINDS = {
  creditNote: {
    subject:  (number) => `Credit Note ${number} – Set-Cast`,
    html:     (cn) => buildCreditNoteEmailHtml(cn),
    filename: (number) => `Gutschrift_${number}.pdf`,
    label:    'Gutschrift',
  },
  cancellation: {
    subject:  (number) => `Cancellation Credit Note ${number} – Set-Cast`,
    html:     (cn) => buildCreditNoteCancellationEmailHtml(cn),
    filename: (number) => `Stornogutschrift_${number}.pdf`,
    label:    'Stornogutschrift',
  },
};

// Convert a Blob to a base64 string (strips the data-URL prefix)
export const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob instanceof Blob ? blob : new Blob([blob]));
  });

/**
 * Email a batch of credit note documents to their talents.
 *   - Fetch full credit note to get talent_id
 *   - Fetch talent by ID to get email address (talent.email || talent.E_Mail)
 *   - to_email is an array: [talentEmail, 'talents@set-cast.com']
 *   - the rendered PDF is attached as base64
 *
 * Never throws — logs and continues, so one failed email can't abort a batch. Returns what
 * happened per note so callers can report honestly instead of assuming success.
 *
 * @param {Array} notes - Items carrying an id (`id` or `creditNoteId`) and ideally a number
 * @param {'creditNote'|'cancellation'} kindName - which document this is
 * @returns {Promise<{sent: string[], failed: string[]}>}
 */
const sendDocuments = async (notes, kindName) => {
  const kind = DOCUMENT_KINDS[kindName];
  const results = { sent: [], failed: [] };

  for (const cnSummary of notes || []) {
    // createFromBookings returns { creditNoteId, creditNoteNumber, bookingIds } — there is NO
    // `id` key. Reading cnSummary.id gave undefined, so this fetched /credit-notes/undefined,
    // got a 400, and the catch below swallowed it: credit notes were created but no Gutschrift
    // email was ever sent (and the UI still said "Email sent"). Accept both shapes.
    const cnId = cnSummary?.id ?? cnSummary?.creditNoteId;
    const ref = cnSummary?.creditNoteNumber || cnSummary?.credit_note_number || cnId || 'unknown';

    try {
      if (!cnId) throw new Error('response carried no credit note id');

      // 1. Fetch full credit note to get talent_id and all details for the template
      const cnRes = await creditNoteService.getCreditNoteById(cnId);
      const cn = cnRes?.data?.creditNote || cnRes?.data || cnSummary;
      const noteNumber = cn.credit_note_number || cn.creditNoteNumber || ref;

      // 2. Fetch talent by ID to get the recipient email address.
      // ALWAYS the talent, deliberately — including agency and provision documents, and
      // including a Storno. Every document of a booking goes to the talent it belongs to; the
      // agency's own address is never used as the recipient here. Confirmed as intended
      // (2026-08), so don't "fix" this into routing agency CNs to the agency.
      const talentId = cn.talent_id || cn.talentId;
      if (!talentId) {
        throw new Error(`no talent_id on credit note ${noteNumber}`);
      }

      const talentRes = await talentService.getTalentById(talentId);
      const talent = talentRes?.data?.talent || talentRes?.data;
      const talentEmail = talent?.email || talent?.E_Mail;

      if (!talentEmail) {
        throw new Error(`talent ${talentId} has no email address`);
      }

      // 3. Fetch the PDF. This is the ZUGFeRD/Factur-X PDF from GET /credit-notes/:id/pdf —
      // the same file the export button downloads, with factur-x.xml embedded inside it. One
      // attachment is therefore enough; a separate .xml would be the identical data twice.
      const pdfRes = await creditNoteService.getCreditNotePdf(cn.id || cnId);
      const pdfBase64 = await blobToBase64(pdfRes.data);

      // 4. Send — document PDF attached
      await emailService.sendEmail({
        to_email: [talentEmail, 'talents@set-cast.com'],
        subject: kind.subject(noteNumber),
        content: kind.html(cn),
        attachments: [
          {
            filename: kind.filename(noteNumber),
            content: pdfBase64,
            encoding: 'base64',
            contentType: 'application/pdf',
          },
        ],
      });

      results.sent.push(String(ref));
    } catch (err) {
      const reason = err.response?.data?.message || err.message || 'unknown error';
      console.error(`${kind.label} email failed for CN ${ref}: ${reason}`, err);
      results.failed.push(`${ref}: ${reason}`);
      // Don't throw — continue with remaining notes
    }
  }

  return results;
};

/**
 * Send the Gutschrift email for freshly created credit notes.
 * Shared by the pending-credit-notes page and the confirmed-orders list, so both entry points
 * send the exact same email.
 *
 * @param {Array} createdNotes - Items from API `data.created`
 * @returns {Promise<{sent: string[], failed: string[]}>}
 */
export const sendCreditNoteEmails = (createdNotes) => sendDocuments(createdNotes, 'creditNote');

/**
 * Send the Stornogutschrift email after a credit note was cancelled.
 * The talent is already holding the original document, so they have to be told it is void and
 * be given the reversal — otherwise their records keep a credit note we no longer recognise.
 *
 * @param {Array} stornos - [{ creditNoteId, creditNoteNumber }] from the cancel response
 * @returns {Promise<{sent: string[], failed: string[]}>}
 */
export const sendCreditNoteCancellationEmails = (stornos) => sendDocuments(stornos, 'cancellation');
